import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { sellersApi } from '../../services/apiService';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import SupplierManageModal from '../../components/SupplierManageModal/SupplierManageModal';
import SupplierProductsModal from '../../components/SupplierProductsModal/SupplierProductsModal';
import SupplierEditModal from '../../components/SupplierEditModal/SupplierEditModal';
import Pagination from '../../components/Pagination/Pagination';
import './SupplierManagePage.css';

const SupplierManagePage = () => {
  const navigate = useNavigate();
  
  // Data states
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showPlanDropdown, setShowPlanDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  
  // Selection states
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  
  // Action dropdown state
  const [actionDropdown, setActionDropdown] = useState({ show: false, supplierId: null });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [editSupplier, setEditSupplier] = useState(null);
  const [loadingSupplier, setLoadingSupplier] = useState(false);

  // Fetch suppliers from API
  const fetchSuppliers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await sellersApi.getAll({ limit: 200 });
      const supplierData = Array.isArray(response) ? response : (response.sellers || []);
      setSuppliers(supplierData);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Failed to fetch suppliers');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  // Filter suppliers
  const filteredSuppliers = suppliers.filter(supplier => {
    const matchesSearch = !searchQuery || 
      supplier.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      supplier.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      supplier.country?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      supplier.city?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCountry = !selectedStatus || supplier.country?.toLowerCase().includes(selectedStatus.toLowerCase());
    const matchesStatus = !selectedPlan || supplier.status?.toLowerCase() === selectedPlan.toLowerCase();
    const matchesCategory = !selectedCategory || supplier.category?.toLowerCase().includes(selectedCategory.toLowerCase()) || supplier.businessType?.toLowerCase().includes(selectedCategory.toLowerCase());
    return matchesSearch && matchesCountry && matchesStatus && matchesCategory;
  });

  // Get unique categories from suppliers
  const uniqueCategories = [...new Set(suppliers.map(s => s.category || s.businessType).filter(Boolean))];

  // Pagination
  const totalPages = Math.ceil(filteredSuppliers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSuppliers = filteredSuppliers.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedStatus, selectedPlan, selectedCategory]);

  // Handle select item
  const handleSelectItem = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Handle select all
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(paginatedSuppliers.map(s => s.id));
    } else {
      setSelectedItems([]);
    }
  };

  // Handle status change
  const handleStatusChange = async (supplierId, newStatus) => {
    try {
      await sellersApi.updateStatus(supplierId, newStatus);
      fetchSuppliers();
      setActionDropdown({ show: false, supplierId: null });
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  // Handle delete supplier
  const handleDeleteSupplier = async (supplierId) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      try {
        await sellersApi.delete(supplierId);
        fetchSuppliers();
        setActionDropdown({ show: false, supplierId: null });
      } catch (err) {
        console.error('Error deleting supplier:', err);
        alert('Failed to delete supplier');
      }
    }
  };

  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      alert('Please select suppliers to delete');
      return;
    }
    if (window.confirm(`Are you sure you want to delete ${selectedItems.length} suppliers?`)) {
      try {
        await sellersApi.bulkDelete(selectedItems);
        setSelectedItems([]);
        fetchSuppliers();
      } catch (err) {
        console.error('Error bulk deleting:', err);
        alert('Failed to delete suppliers');
      }
    }
  };

  // Handle bulk status update
  const handleBulkStatusUpdate = async (status) => {
    if (selectedItems.length === 0) {
      alert('Please select suppliers to update');
      return;
    }
    try {
      await sellersApi.bulkUpdateStatus(selectedItems, status);
      setSelectedItems([]);
      fetchSuppliers();
    } catch (err) {
      console.error('Error bulk updating:', err);
      alert('Failed to update suppliers');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { 
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  // Handle edit supplier
  const handleEditSupplier = async (supplier) => {
    try {
      setActionDropdown({ show: false, supplierId: null });
      // Fetch full supplier data
      const fullSupplierData = await sellersApi.getById(supplier.id);
      setEditSupplier(fullSupplierData);
      setShowEditModal(true);
    } catch (err) {
      console.error('Error loading supplier for edit:', err);
      alert('Failed to load supplier details');
    }
  };

  // Handle edit modal update
  const handleEditUpdate = () => {
    fetchSuppliers();
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.sm-dropdown') && !e.target.closest('.sm-category-dropdown')) {
        setShowStatusDropdown(false);
        setShowPlanDropdown(false);
        setShowCategoryDropdown(false);
      }
      if (!e.target.closest('.sm-action-dropdown')) {
        setActionDropdown({ show: false, supplierId: null });
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Generate page numbers
  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= Math.min(totalPages, 5); i++) {
      pages.push(i);
    }
    return pages;
  };

  // Export suppliers to CSV
  const handleExport = () => {
    const dataToPrint = selectedItems.length > 0 
      ? suppliers.filter(s => selectedItems.includes(s.id))
      : filteredSuppliers;
    
    if (dataToPrint.length === 0) {
      alert('No data to print');
      return;
    }

    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Supplier List - Print</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1F396C; margin-bottom: 20px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; font-size: 12px; }
          th { background: #E6EBEF; color: #374151; font-weight: 600; }
          tr:nth-child(even) { background: #f9f9f9; }
          .status-badge { padding: 4px 8px; border-radius: 12px; font-size: 11px; display: inline-block; }
          .status-active { background: #DCFCE7; color: #006928; }
          .status-pending { background: #FFEDD5; color: #691C00; }
          .status-inactive { background: #E5E5E5; color: #6D6D6D; }
          .status-suspended { background: #FEE2E2; color: #DC2626; }
          .print-date { color: #666; font-size: 12px; margin-bottom: 10px; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>Supplier List</h1>
        <p class="print-date">Generated on: ${new Date().toLocaleString()}</p>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Company Name</th>
              <th>Business Type</th>
              <th>Category</th>
              <th>Location</th>
              <th>Email</th>
              <th>Products</th>
              <th>Membership</th>
              <th>Status</th>
              <th>Created Date</th>
            </tr>
          </thead>
          <tbody>
            ${dataToPrint.map((supplier, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${supplier.name || 'N/A'}</td>
                <td>${supplier.businessType || 'Manufacturer'}</td>
                <td>${supplier.category || 'N/A'}</td>
                <td>${supplier.city ? `${supplier.city}, ${supplier.country || ''}` : (supplier.country || 'N/A')}</td>
                <td>${supplier.email || 'N/A'}</td>
                <td>${supplier._count?.products || 0}</td>
                <td>${supplier.membershipPlan || 'Basic'}</td>
                <td><span class="status-badge status-${(supplier.status || 'pending').toLowerCase()}">${supplier.status || 'Pending'}</span></td>
                <td>${formatDate(supplier.createdAt)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `;
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedStatus('');
    setSelectedPlan('');
    setSelectedCategory('');
    setCurrentPage(1);
  };

  // Open supplier details modal
  const handleOpenDetails = async (supplier) => {
    try {
      setLoadingSupplier(true);
      setShowModal(true); // Show modal immediately with loading state
      // Fetch full supplier details with products
      console.log('Fetching supplier details for ID:', supplier.id);
      const fullSupplierData = await sellersApi.getById(supplier.id);
      console.log('Full supplier data received:', fullSupplierData);
      console.log('Products:', fullSupplierData.products);
      setSelectedSupplier(fullSupplierData);
    } catch (err) {
      console.error('Error fetching supplier details:', err);
      // Fall back to basic supplier data
      setSelectedSupplier(supplier);
    } finally {
      setLoadingSupplier(false);
    }
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedSupplier(null);
  };

  return (
    <AdminLayout>
      <div className="supplier-manage-content">
        <div className="sm-page-header">
          <div className="sm-page-title-section">
            <h1 className="sm-page-title">Supplier Manage</h1>
            <p className="sm-page-description">Effortlessly oversee and update your supplier profiles, products, and verifications in one place.</p>
          </div>
          <div className="sm-header-actions">
            {selectedItems.length > 0 && (
              <>
                <button className="sm-bulk-btn activate" onClick={() => handleBulkStatusUpdate('Active')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Activate ({selectedItems.length})
                </button>
                <button className="sm-bulk-btn hide" onClick={() => handleBulkStatusUpdate('Hidden')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Hide ({selectedItems.length})
                </button>
                <button className="sm-bulk-btn suspend" onClick={() => handleBulkStatusUpdate('Suspended')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Suspend ({selectedItems.length})
                </button>
                <button className="sm-bulk-btn delete" onClick={handleBulkDelete}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6h18M8 6V4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2v2m3 0v14c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2V6h14z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Delete ({selectedItems.length})
                </button>
              </>
            )}
          </div>
        </div>

        <div className="sm-content">
          <div className="sm-list-header">
            <h2 className="sm-list-title">Supplier Lists</h2>
            <div className="sm-list-controls">
              <div className="sm-search-box">
                <input 
                  type="text" 
                  placeholder="Search..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <circle cx="9.16667" cy="9.16667" r="5.83333" stroke="#969696" strokeWidth="1.5"/>
                  <path d="M14.166 14.1665L17.4993 17.4998" stroke="#969696" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              
              {/* Country Filter - Native Select */}
              <div className="sm-select-wrapper">
                <select 
                  className="sm-filter-select"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value="">Country</option>
                  <option value="UAE">UAE</option>
                  <option value="Saudi Arabia">Saudi Arabia</option>
                  <option value="Qatar">Qatar</option>
                  <option value="Kuwait">Kuwait</option>
                </select>
              </div>
              
              {/* Status Filter - Native Select */}
              <div className="sm-select-wrapper">
                <select 
                  className="sm-filter-select"
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                >
                  <option value="">Status</option>
                  <option value="Active">Active</option>
                  <option value="Pending">Pending</option>
                  <option value="Hidden">Hidden</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Suspended">Suspended</option>
                </select>
              </div>
              
              {/* Filter By Category - Native Select */}
              <div className="sm-select-wrapper sm-category-select">
                <svg className="sm-filter-icon" width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M18.0171 3.86955C17.9209 3.64729 17.7615 3.45822 17.5587 3.32584C17.3559 3.19347 17.1186 3.12363 16.8764 3.12502H3.12644C2.88452 3.1255 2.64792 3.19617 2.44537 3.32846C2.24282 3.46076 2.08301 3.64899 1.98532 3.87032C1.88764 4.09165 1.85629 4.33658 1.89507 4.57538C1.93385 4.81418 2.04109 5.03659 2.20379 5.21565L2.21004 5.22268L7.50144 10.8727V16.875C7.50139 17.1013 7.56274 17.3233 7.67894 17.5174C7.79514 17.7115 7.96185 17.8704 8.16128 17.9772C8.36071 18.084 8.58539 18.1347 8.81136 18.1239C9.03733 18.113 9.25613 18.0411 9.44441 17.9156L11.9444 16.2485C12.1158 16.1343 12.2563 15.9796 12.3535 15.798C12.4507 15.6165 12.5015 15.4137 12.5014 15.2078V10.8727L17.7936 5.22268L17.7999 5.21565C17.9643 5.03741 18.0726 4.8147 18.1112 4.57531C18.1498 4.33592 18.1171 4.09045 18.0171 3.86955Z" fill="#333333"/>
                </svg>
                <select 
                  className="sm-filter-select with-icon"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">Filter By Category</option>
                  <option value="Manufacturer">Manufacturer</option>
                  <option value="Distributor">Distributor</option>
                  <option value="Supplier">Supplier</option>
                  <option value="Trading Company">Trading Company</option>
                  <option value="Contractor">Contractor</option>
                  {uniqueCategories.filter(cat => !['Manufacturer', 'Distributor', 'Supplier', 'Trading Company', 'Contractor'].includes(cat)).map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              
              {(searchQuery || selectedStatus || selectedPlan || selectedCategory) && (
                <button className="sm-clear-btn" onClick={handleClearFilters}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Clear</span>
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="sm-loading">Loading suppliers...</div>
          ) : error ? (
            <div className="sm-error">
              {error}
              <button onClick={fetchSuppliers}>Retry</button>
            </div>
          ) : (
            <>
              <div className="sm-table-container">
                <div className="sm-table">
                  <div className="sm-table-header">
                    <div className="sm-th sm-th-checkbox">
                      <input 
                        type="checkbox" 
                        checked={selectedItems.length === paginatedSuppliers.length && paginatedSuppliers.length > 0}
                        onChange={handleSelectAll}
                      />
                      <span>All</span>
                    </div>
                    <div className="sm-th">Logo</div>
                    <div className="sm-th">Creation date</div>
                    <div className="sm-th">Company Name</div>
                    <div className="sm-th">Company Type</div>
                    <div className="sm-th">Location</div>
                    <div className="sm-th">Verifications</div>
                    <div className="sm-th">Gmail</div>
                    <div className="sm-th">Products</div>
                    <div className="sm-th">Membership</div>
                    <div className="sm-th">Statues</div>
                    <div className="sm-th">Details</div>
                    <div className="sm-th">Action</div>
                  </div>
                  <div className="sm-table-body">
                    {paginatedSuppliers.length === 0 ? (
                      <div className="sm-no-data">No suppliers found</div>
                    ) : (
                      paginatedSuppliers.map((supplier) => (
                        <div key={supplier.id} className="sm-table-row">
                          <div className="sm-td sm-td-checkbox">
                            <input 
                              type="checkbox" 
                              checked={selectedItems.includes(supplier.id)}
                              onChange={() => handleSelectItem(supplier.id)}
                            />
                          </div>
                          <div className="sm-td sm-td-logo">
                            <div className="sm-logo-wrapper">
                              {supplier.logo ? (
                                <img src={supplier.logo} alt={supplier.name} className="sm-logo-img" />
                              ) : (
                                <div className="sm-logo-placeholder">
                                  {supplier.name?.charAt(0) || 'S'}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="sm-td sm-td-date">{formatDate(supplier.createdAt)}</div>
                          <div className="sm-td sm-td-company">{supplier.name || 'N/A'}</div>
                          <div className="sm-td sm-td-type">{supplier.businessType || 'Manufacturer'}</div>
                          <div className="sm-td sm-td-location">{supplier.city ? `${supplier.city}, ${supplier.country || ''}` : (supplier.country || 'N/A')}</div>
                          <div className="sm-td sm-td-verification">
                            <span className="sm-verification-text">
                              {supplier.verificationLevel === 'verified' ? 'Verified' : 
                               supplier.verificationLevel === 'inspected' ? 'Inspected' : 
                               supplier.verificationLevel === 'certified' ? 'Certified' : 'Verified'}
                            </span>
                          </div>
                          <div className="sm-td sm-td-email">{supplier.email || 'N/A'}</div>
                          <div className="sm-td sm-td-products">{supplier._count?.products || 0}</div>
                          <div className="sm-td sm-td-membership">
                            <span className="sm-membership-text">
                              {supplier.membershipPlan || 'Basic'}
                            </span>
                          </div>
                          <div className="sm-td sm-td-status">
                            <span className={`sm-status-badge ${(supplier.status || 'pending').toLowerCase()}`}>
                              {supplier.status || 'Pending'}
                            </span>
                          </div>
                          <div className="sm-td sm-td-details">
                            <button 
                              className="sm-details-btn"
                              onClick={() => handleOpenDetails(supplier)}
                            >
                              Details
                            </button>
                          </div>
                          <div className="sm-td sm-td-action">
                            <div className="sm-action-dropdown">
                              <button 
                                className="sm-action-btn"
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setActionDropdown({ 
                                    show: actionDropdown.supplierId === supplier.id ? !actionDropdown.show : true, 
                                    supplierId: supplier.id 
                                  }); 
                                }}
                              >
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                  <circle cx="10" cy="4" r="1.5" fill="#6D6D6D"/>
                                  <circle cx="10" cy="10" r="1.5" fill="#6D6D6D"/>
                                  <circle cx="10" cy="16" r="1.5" fill="#6D6D6D"/>
                                </svg>
                              </button>
                              {actionDropdown.show && actionDropdown.supplierId === supplier.id && (
                                <div className="sm-action-menu">
                                  <div className="sm-action-item" onClick={() => navigate(`/seller/${supplier.slug || supplier.id}`)}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/>
                                      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                    View
                                  </div>
                                  <div className="sm-action-item" onClick={() => handleEditSupplier(supplier)}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Edit
                                  </div>
                                  <div className="sm-action-item" onClick={() => handleStatusChange(supplier.id, 'Active')}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M20 6L9 17L4 12" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Activate
                                  </div>
                                  <div className="sm-action-item" onClick={() => handleStatusChange(supplier.id, 'Hidden')}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <line x1="1" y1="1" x2="23" y2="23" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Hide
                                  </div>
                                  <div className="sm-action-item" onClick={() => handleStatusChange(supplier.id, 'Suspended')}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
                                      <path d="M4.93 4.93l14.14 14.14" stroke="#ef4444" strokeWidth="2"/>
                                    </svg>
                                    Suspend
                                  </div>
                                  <div className="sm-action-item delete" onClick={() => handleDeleteSupplier(supplier.id)}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M3 6h18M8 6V4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2v2m3 0v14c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2V6h14z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Delete
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </>
          )}
        </div>
      </div>

      {/* Supplier Details Modal */}
      <SupplierManageModal 
        isOpen={showModal} 
        onClose={handleCloseModal} 
        supplier={selectedSupplier}
        loading={loadingSupplier}
        onViewProducts={() => {
          setShowModal(false);
          setShowProductsModal(true);
        }}
      />

      {/* Supplier Products Modal */}
      <SupplierProductsModal
        isOpen={showProductsModal}
        onClose={() => setShowProductsModal(false)}
        supplier={selectedSupplier}
      />

      {/* Supplier Edit Modal */}
      <SupplierEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        supplier={editSupplier}
        onUpdate={handleEditUpdate}
      />
    </AdminLayout>
  );
};

export default SupplierManagePage;
